import {
  AfterViewInit,
  Component,
  Input,
  viewChild,
  inject,
  input,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ScrollPanel } from '@openng/optimus-ui/scrollpanel';
import { DeviceService } from 'src/app/services/device.service';
import { AppComponent } from '../../../app.component';
import { TabService } from '../../services/tab.service';
import { MenuItem } from '@openng/optimus-ui/api';
import { TranslateDirective } from '@ngx-translate/core';
import { menuContainerMapping, MenuRoute } from './menu-mapping';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';

@Component({
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
  styleUrls: ['./app.menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ScrollPanel,
    forwardRef(() => AppSubMenuComponent),
    TranslateDirective,
  ],
})
export class AppMenuComponent implements AfterViewInit {
  protected _app = inject(AppComponent);

  readonly reset = input<boolean>(true);
  readonly parentActive = input<boolean>(false);
  readonly version = input<string>();

  private model: any[] = [];
  @Input() set menuItems(items: any[]) {
    this.model = items;
  }

  get menuItems() {
    return this.model;
  }

  readonly layoutMenuScrollerViewChild =
    viewChild<ScrollPanel>('layoutMenuScroller');

  ngAfterViewInit() {
    setTimeout(() => {
      this.layoutMenuScrollerViewChild()?.moveBar();
    }, 100);
  }
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-submenu]',
  template: `
    @for (child of getCorrectItem(); track child; let i = $index) {
      @if (child.visible === false ? false : true) {
        <li
          [class.active-menuitem]="isActive(i)"
          [class]="child.badgeStyleClass"
        >
          @if (!child.routerLink) {
            <a
              [href]="child.url || '#'"
              (click)="itemClick($event, child, i)"
              (mouseenter)="onMouseEnter(i)"
              [class]="[child.styleClass]"
              [attr.tabindex]="!isActive(i) ? '-1' : null"
              [attr.target]="child.target"
            >
              <span>{{ child.label }}</span>
              @if (child.items) {
                <i class="fa fa-fw fa-angle-down layout-menuitem-toggler"></i>
              }
            </a>
          }

          @if (child.routerLink) {
            <a
              (click)="itemClick($event, child, i)"
              (mouseenter)="onMouseEnter(i)"
              [attr.tabindex]="!isActive(i) ? '-1' : null"
              [attr.target]="child.target"
            >
              <span>{{ child.label }}</span>
              @if (child.items) {
                <i class="fa fa-fw fa-angle-down layout-menuitem-toggler"></i>
              }
            </a>
          }

          <div class="layout-menu-tooltip">
            <div class="layout-menu-tooltip-arrow"></div>
            <div class="layout-menu-tooltip-text">{{ child.label }}</div>
          </div>

          @if (child.items && isActive(i)) {
            <ul
              app-submenu
              [item]="child"
              [visible]="true"
              [reset]="true"
              [parentActive]="true"
              class="submenu submenu--expanded"
            ></ul>
          }
        </li>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    .submenu {
      overflow: hidden;
      animation: submenuExpand 400ms cubic-bezier(0.86, 0, 0.07, 1);
    }

    @keyframes submenuExpand {
      from {
        opacity: 0;
        transform: translateY(-4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,
})
export class AppSubMenuComponent {
  protected _app = inject(AppComponent);
  protected _appMenu = inject(AppMenuComponent);
  protected _deviceService = inject(DeviceService);
  protected _tabService = inject(TabService);
  protected _logAndMessageService = inject(LogAndMessageService);

  readonly item = input<MenuItem>();
  readonly root = input<boolean>();
  readonly visible = input<boolean>();

  _parentActive!: boolean;
  _reset!: boolean;

  activeIndex!: number | null;

  itemClick(event: Event, item: MenuItem, index: number) {
    // avoid processing disabled items
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    // activate current item and deactivate active sibling if any
    this.activeIndex = this.activeIndex === index ? null : index;

    // execute command
    if (item.command) {
      item.command({ originalEvent: event, item: item });
    }

    // prevent hash change
    if (item.items || (!item.url && !item.routerLink)) {
      setTimeout(() => {
        this._appMenu.layoutMenuScrollerViewChild()?.moveBar();
      }, 450);
      event.preventDefault();
    }

    if (item.routerLink) {
      const containerToBuild =
        menuContainerMapping[item.routerLink as MenuRoute];

      if (!containerToBuild) {
        this._logAndMessageService.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'COMMON.MESSAGES.ERROR.FAILED_TO_BUILD_TAB',
        });
        console.error('Router link:', item.routerLink);
        return;
      }

      const newTab = this._tabService.buildNewTab(containerToBuild, true);
      if (!this._deviceService.isMobile()) {
        this._tabService.openTab(newTab);
      } else {
        this._tabService.setMobileTab(newTab);
      }
    }
  }

  onMouseEnter(index: number) {}

  isActive(index: number): boolean {
    return this.activeIndex === index;
  }

  @Input() get reset(): boolean {
    return this._reset;
  }

  set reset(val: boolean) {
    this._reset = val;
  }

  @Input() get parentActive(): boolean {
    return this._parentActive;
  }

  set parentActive(val: boolean) {
    this._parentActive = val;
    if (!this._parentActive) {
      this.activeIndex = null;
    } else {
      this.activeIndex = 0;
    }
  }

  // Turning on strict templates with this check directly embedded did not work,
  // we need to have it here returning any to get past the ngIterable type check
  // while still allowing this to actually work.
  getCorrectItem(): any {
    return this.root() ? this.item() : this.item()?.items;
  }
}
